// Team check-ins as a roster: everyone who worked, who has checked in, what they flagged.
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { Loader2, Sparkles, BellRing } from 'lucide-react';
import { supabase } from '@/src/lib/supabase';
import { useAppColors } from '@/src/lib/theme';
import { AI_FN_SUFFIX } from '@/src/lib/aiFunctions';
import { CHECKIN_FLAGS, RATINGS, checkinLines, flagByKey, longDay, timeOf } from '@/src/lib/checkin';
import type { EodQuestion } from '@/src/hooks/useEodReports';
import type { NightCheckin, NightPerson } from '@/src/hooks/useEodNight';
import { Avatar, BackButton, Chip, DateStepper, Panel } from './eod-ui';
import { useMyLanguage } from '@/src/hooks/useMyLanguage';
import { useCheckinTranslations } from '@/src/hooks/useCheckinTranslations';
import { languageByCode } from '@/src/lib/languages';

interface Props {
  venueId: string;
  date: string;
  maxDate: string;
  onDate: (d: string) => void;
  tz: string | null;
  checkins: NightCheckin[];
  people: NightPerson[];
  loading: boolean;
  remind: (ids: string[]) => Promise<{ sent?: number; error?: string }>;
  onBack: () => void;
}

export interface NightSummary {
  headline: string;
  action: Array<{ text: string; who?: string }>;
  service: Array<{ when: string; text: string }>;
  guests: string[];
  team: string[];
}

function parseSummary(raw: string): NightSummary | null {
  const a = raw.indexOf('{'), b = raw.lastIndexOf('}');
  if (a < 0 || b <= a) return null;
  try {
    const j = JSON.parse(raw.slice(a, b + 1));
    const arr = (x: unknown) => (Array.isArray(x) ? x : []);
    return {
      headline: String(j.headline ?? '').trim(),
      action: arr(j.action).map((x: { text?: string; who?: string } | string) => (typeof x === 'string' ? { text: x } : { text: String(x.text ?? ''), who: x.who ? String(x.who) : undefined })).filter((x) => x.text),
      service: arr(j.service).map((x: { when?: string; text?: string }) => ({ when: String(x.when ?? ''), text: String(x.text ?? '') })).filter((x) => x.text),
      guests: arr(j.guests).map(String).filter(Boolean),
      team: arr(j.team).map(String).filter(Boolean),
    };
  } catch { return null; }
}

async function generateSummary(rows: NightCheckin[], labels: Record<string, string>, date: string, language: string): Promise<NightSummary | string> {
  const { data: s } = await supabase.auth.getSession();
  const token = s.session?.access_token;
  if (!token) throw new Error('Not signed in');
  const content = rows.map((r) => `\n${r.name} (${r.position ?? r.role}):\n${checkinLines(r.answers, labels).map(([k, v]) => `  ${k}: ${v}`).join('\n')}`).join('\n');
  const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat${AI_FN_SUFFIX}?forceFunctionRegion=ap-southeast-2`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      message: `Summarise these end of day staff check-ins from ${date} for the venue manager. Write in ${language} (the notes may be in other languages; translate them). Keep the details that matter: times, peak periods, covers, table numbers, group sizes, amounts, dishes and drinks, dietary needs, names. Merge what different people said about the same thing; never contradict yourself (if there is a problem, don't call the night issue-free). Only use what is written below; don't invent numbers. Do not call any tools.

Reply with JSON only, no other text:
{"headline":"one sentence: how the night went overall",
 "action":[{"text":"something the manager should deal with: complaint, broken equipment, sold out, staffing, safety","who":"first name of who flagged it"}],
 "service":[{"when":"Lunch / Happy hour / Dinner / or a time like 4pm","text":"how that part of the day went, with busy times, covers and what sold"}],
 "guests":["notable tables and guests: regulars, groups, birthdays, dietary needs, feedback, with table numbers and amounts"],
 "team":["training, who did what, staffing notes"]}
Use empty lists when there's nothing. Each item one or two short sentences.
${content}`,
      drawerId: 'reports', venueName: 'venue', history: [],
    }),
  });
  const reader = resp.body?.getReader();
  if (!reader) throw new Error('No answer');
  const dec = new TextDecoder();
  let out = '';
  // A line can be split across two chunks: keep the unfinished end and join it to the next chunk,
  // otherwise words go missing from the summary.
  let buf = '';
  const take = (line: string) => {
    if (!line.startsWith('data: ')) return;
    try { const j = JSON.parse(line.slice(6)); if (j.type === 'text' && j.text) out += j.text; } catch { /* not JSON */ }
  };
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const lines = buf.split('\n');
    buf = lines.pop() ?? '';
    for (const line of lines) take(line);
  }
  take(buf + dec.decode());
  return parseSummary(out) ?? (out.trim() || 'Could not write a summary.');
}

export default function EodManagerPage({ venueId, date, maxDate, onDate, tz, checkins, people, loading, remind, onBack }: Props) {
  const c = useAppColors();
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [summary, setSummary] = useState<{ busy: boolean; text: NightSummary | string | null; error: string | null }>({ busy: false, text: null, error: null });
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [nudge, setNudge] = useState<{ busy: boolean; msg: string | null }>({ busy: false, msg: null });
  const { language } = useMyLanguage();
  const { translations } = useCheckinTranslations(checkins, language);
  const [original, setOriginal] = useState<Set<string>>(new Set());
  const langName = languageByCode(language)?.name ?? 'English';

  useEffect(() => {
    void supabase.from('venues').select('eod_questions').eq('id', venueId).maybeSingle().then(({ data }) => {
      const q = (data?.eod_questions as Record<string, EodQuestion[]> | null) ?? {};
      const m: Record<string, string> = {};
      for (const list of Object.values(q)) for (const x of list ?? []) m[x.key] = x.label;
      setLabels(m);
    });
  }, [venueId]);
  useEffect(() => { setSummary({ busy: false, text: null, error: null }); setNudge({ busy: false, msg: null }); }, [date]);

  const isTonight = date === maxDate;
  const byUser = useMemo(() => new Map(checkins.map((r) => [r.userId, r])), [checkins]);
  // People without an OpsAlo login worked too, but can't send a check-in: listed separately.
  const missing = people.filter((p) => !byUser.has(p.userId) && p.onOpsAlo !== false);
  const noLogin = people.filter((p) => !byUser.has(p.userId) && p.onOpsAlo === false);
  const total = new Set([...people.filter((p) => p.onOpsAlo !== false).map((p) => p.userId), ...checkins.map((r) => r.userId)]).size;
  const shiftOf = (id: string) => people.find((p) => p.userId === id);

  const flagCounts = CHECKIN_FLAGS.map((f) => ({ f, n: checkins.filter((r) => r.checkin.flags.includes(f.key)).length })).filter((x) => x.n > 0);

  const doRemind = async (ids: string[]) => {
    setNudge({ busy: true, msg: null });
    const r = await remind(ids);
    setNudge({ busy: false, msg: r.error ?? (r.sent ? `Reminder sent to ${r.sent} ${r.sent === 1 ? 'person' : 'people'}.` : 'Already reminded in the last 2 hours.') });
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      className="px-4 sm:px-8 py-6 max-w-4xl mx-auto w-full space-y-5 pb-24">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <BackButton onClick={onBack} label="Back to tonight" />
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight" style={{ color: c.text }}>Team check-ins</h1>
            <p className="text-[13px]" style={{ color: c.textDim }}>{longDay(date)}{total ? ` · ${checkins.length} of ${total} in` : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DateStepper date={date} max={maxDate} onChange={onDate} />
          {isTonight && missing.length > 0 && (
            <button onClick={() => void doRemind(missing.map((p) => p.userId))} disabled={nudge.busy}
              className="h-10 px-3.5 rounded-xl text-sm font-semibold flex items-center gap-2 disabled:opacity-50" style={{ background: c.bgElevated, border: `1px solid ${c.border}`, color: c.text }}>
              <BellRing size={15} /> Remind {missing.length === 1 ? '1 person' : `all ${missing.length}`}
            </button>
          )}
        </div>
      </header>
      {nudge.msg && <p className="text-sm" style={{ color: /need|error|denied/i.test(nudge.msg) ? c.danger : c.accentSoft }}>{nudge.msg}</p>}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 size={20} className="animate-spin" style={{ color: c.textFaint }} /></div>
      ) : total === 0 && noLogin.length === 0 ? (
        <Panel className="p-10 text-center">
          <p className="text-base font-semibold" style={{ color: c.text }}>Nobody on the roster or checked in for this day</p>
          <p className="text-sm mt-1" style={{ color: c.textDim }}>Check-ins show up here as people send them.</p>
        </Panel>
      ) : (
        <>
          {checkins.length > 0 && (
            <Panel className="p-5 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: c.textDim }}>Summary</p>
                {!summary.text && (
                  <button disabled={summary.busy} onClick={async () => {
                    setSummary({ busy: true, text: null, error: null });
                    try { setSummary({ busy: false, text: await generateSummary(checkins, labels, longDay(date), langName), error: null }); }
                    catch (e) { setSummary({ busy: false, text: null, error: e instanceof Error ? e.message : 'Failed' }); }
                  }} className="h-9 px-3 rounded-lg text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50" style={{ background: c.accentBg, color: c.accentSoft }}>
                    {summary.busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} {summary.busy ? 'Writing…' : 'Summarise the night'}
                  </button>
                )}
              </div>
              {typeof summary.text === 'string' && <p className="text-[15px] leading-relaxed whitespace-pre-wrap" style={{ color: c.text }}>{summary.text}</p>}
              {summary.text && typeof summary.text === 'object' && <SummaryView s={summary.text} onRedo={() => setSummary({ busy: false, text: null, error: null })} />}
              {summary.error && <p className="text-sm" style={{ color: c.danger }}>{summary.error}</p>}
              <div className="flex flex-wrap gap-2">
                {flagCounts.length === 0 ? <Chip tone="green">Nothing flagged</Chip> : flagCounts.map(({ f, n }) => <Chip key={f.key} tone={f.tone}>{n} {f.short.toLowerCase()}</Chip>)}
              </div>
            </Panel>
          )}

          <Panel className="overflow-hidden">
            {checkins.map((r, i) => {
              const s = shiftOf(r.userId);
              const tr = translations[r.id];
              const showTr = !!tr?.from && !original.has(r.id);
              const lines = checkinLines(showTr ? tr!.answers : r.answers, labels).filter(([k]) => k !== 'Night');
              const expanded = open.has(r.id);
              const shown = expanded ? lines : lines.slice(0, 2);
              const flagged = r.checkin.flags.length > 0;
              return (
                <div key={r.id} className="flex gap-3.5 px-4 sm:px-5 py-4" style={{ borderTop: i ? `1px solid ${c.border}` : undefined, background: flagged ? (c.isDark ? 'rgba(255,255,255,0.02)' : '#FBFAF8') : undefined }}>
                  <Avatar name={r.name} size={40} />
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span className="text-[15px] font-bold" style={{ color: c.text }}>{r.name}</span>
                      <span className="text-[13px]" style={{ color: c.textDim }}>{[s?.area ?? r.position, s ? `${s.start}–${s.end}` : null].filter(Boolean).join(' · ')}</span>
                      <span className="ml-auto text-[13px] font-semibold" style={{ color: c.accentSoft }}>Sent {timeOf(r.updatedAt ?? r.submittedAt, tz)}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {r.checkin.rating && <Chip tone="grey">Night: {RATINGS.find((x) => x.key === r.checkin.rating)?.label}</Chip>}
                      {r.checkin.flags.map((f) => <Chip key={f} tone={flagByKey(f)?.tone ?? 'grey'}>{flagByKey(f)?.short ?? f}</Chip>)}
                      {!r.checkin.rating && !flagged && lines.length === 0 && <Chip tone="grey">Nothing to flag</Chip>}
                    </div>
                    {shown.length > 0 && (
                      <div className="space-y-1.5">
                        {shown.map(([k, v], j) => (
                          <p key={j} className="text-sm leading-relaxed" style={{ color: c.text }}>
                            <span className="font-semibold" style={{ color: c.textDim }}>{k}: </span>{v}
                          </p>
                        ))}
                      </div>
                    )}
                    {tr?.from && (
                      <p className="text-[12px]" style={{ color: c.textDim }}>
                        {showTr ? `Translated from ${tr.from}` : `Written in ${tr.from}`} ·{' '}
                        <button onClick={() => setOriginal((o) => { const n = new Set(o); if (n.has(r.id)) n.delete(r.id); else n.add(r.id); return n; })}
                          className="font-semibold underline" style={{ color: c.accentSoft }}>{showTr ? 'Show original' : `Show in ${langName}`}</button>
                      </p>
                    )}
                    {lines.length > 2 && (
                      <button onClick={() => setOpen((o) => { const n = new Set(o); if (n.has(r.id)) n.delete(r.id); else n.add(r.id); return n; })}
                        className="text-[13px] font-semibold" style={{ color: c.accentSoft }}>
                        {expanded ? 'Show less' : `Show all ${lines.length}`}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {missing.map((p, i) => (
              <div key={p.userId} className="flex items-center gap-3.5 px-4 sm:px-5 py-3.5" style={{ borderTop: checkins.length || i ? `1px solid ${c.border}` : undefined }}>
                <Avatar name={p.name} size={40} pending />
                <div className="flex-1 min-w-0">
                  <p className="text-[15px] font-semibold truncate" style={{ color: c.text }}>{p.name}</p>
                  <p className="text-[13px] truncate" style={{ color: c.textDim }}>
                    {[p.area, isTonight ? (new Date(p.endIso) > new Date() ? `finishes ${p.end}` : `finished ${p.end}`) : `${p.start}–${p.end}`].filter(Boolean).join(' · ')}
                  </p>
                </div>
                {isTonight ? (
                  <button onClick={() => void doRemind([p.userId])} disabled={nudge.busy}
                    className="h-9 px-3 rounded-lg text-[13px] font-semibold disabled:opacity-50" style={{ background: c.accentBg, color: c.accentSoft }}>Remind</button>
                ) : <span className="text-[13px]" style={{ color: c.textDim }}>Didn't send</span>}
              </div>
            ))}
            {noLogin.length > 0 && (
              <div className="px-4 sm:px-5 py-3.5" style={{ borderTop: checkins.length || missing.length ? `1px solid ${c.border}` : undefined }}>
                <p className="text-[12px] font-bold uppercase tracking-widest mb-2" style={{ color: c.textFaint }}>Also worked · not on OpsAlo yet</p>
                <div className="flex flex-wrap gap-2">
                  {noLogin.map((p) => (
                    <span key={p.userId} className="text-[13px] px-2.5 py-1 rounded-full" style={{ background: c.iconBg, color: c.textDim }}>
                      {p.name}{isTonight ? '' : ` · ${p.start}–${p.end}`}
                    </span>
                  ))}
                </div>
                <p className="text-[12px] mt-2" style={{ color: c.textFaint }}>Invite them from Team access so they can send check-ins.</p>
              </div>
            )}
          </Panel>
        </>
      )}
    </motion.div>
  );
}

function SummaryView({ s, onRedo }: { s: NightSummary; onRedo: () => void }) {
  const c = useAppColors();
  const head = (t: string, tone?: string) => <p className="text-[11px] font-bold uppercase tracking-widest mb-1.5" style={{ color: tone ?? c.textDim }}>{t}</p>;
  const warn = c.isDark ? '#FCD38D' : '#8A4B08';
  return (
    <div className="space-y-4">
      {s.headline && <p className="text-[16px] font-semibold leading-snug" style={{ color: c.text }}>{s.headline}</p>}
      {s.action.length > 0 && (
        <div className="rounded-xl p-3.5" style={{ background: c.isDark ? 'rgba(232,160,32,0.12)' : '#FCEFD9' }}>
          {head('Needs action', warn)}
          <ul className="space-y-1.5">
            {s.action.map((a, i) => (
              <li key={i} className="text-sm leading-relaxed flex gap-2" style={{ color: c.text }}>
                <span aria-hidden="true" style={{ color: warn }}>•</span>
                <span>{a.text}{a.who && <span style={{ color: c.textDim }}> · {a.who}</span>}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {s.service.length > 0 && (
        <div>
          {head('Service')}
          <div className="space-y-2">
            {s.service.map((x, i) => (
              <div key={i} className="grid gap-x-3 text-sm leading-relaxed" style={{ gridTemplateColumns: 'minmax(84px, max-content) 1fr' }}>
                <span className="font-semibold" style={{ color: c.text }}>{x.when}</span>
                <span style={{ color: c.text }}>{x.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {[['Guests', s.guests], ['Team', s.team]].map(([t, items]) => (items as string[]).length > 0 && (
        <div key={t as string}>
          {head(t as string)}
          <ul className="space-y-1.5">
            {(items as string[]).map((x, i) => (
              <li key={i} className="text-sm leading-relaxed flex gap-2" style={{ color: c.text }}><span aria-hidden="true" style={{ color: c.textDim }}>•</span><span>{x}</span></li>
            ))}
          </ul>
        </div>
      ))}
      <button onClick={onRedo} className="text-[13px] font-semibold" style={{ color: c.accentSoft }}>Write it again</button>
    </div>
  );
}
